import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { HelpButton } from '@/components/HelpButton';
import { EmptyState } from '@/components/EmptyState';
import { HelpText, PageHint } from '@/components/HelpText';
import { Modal } from '@/components/Modal';
import {
  matchesOwner,
  OwnerBadge,
  OwnerFilter,
  useOwnership,
  type OwnerFilterValue,
} from '@/components/OwnerBadge';
import { useToast } from '@/components/Toast';
import {
  BRAND_LABEL,
  STATUS_LABEL,
  type CardBrand,
  type CardSummary,
  type ClosingMode,
  createCard,
  listCards,
  updateCard,
} from '@/lib/cards';
import { deriveAccountName, fmtBRL, listInstitutions, updateAccount } from '@/lib/finance';

export function CardsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const cardsQ = useQuery({ queryKey: ['cards'], queryFn: listCards });
  const institutionsQ = useQuery({ queryKey: ['institutions'], queryFn: listInstitutions });
  const { myId } = useOwnership();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CardSummary | null>(null);
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilterValue>('all');

  const visibleCards = (cardsQ.data ?? []).filter((c) =>
    matchesOwner(ownerFilter, c.owner_user_id, c.is_shared, myId),
  );

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold">Cartões</h1>
          <HelpButton topic="cartoes" />
        </div>
        <button
          onClick={() => setOpen(true)}
          className="tap rounded-md bg-slate-900 px-3 py-2 text-sm font-medium text-white"
        >
          + novo
        </button>
      </header>

      <PageHint>
        Cartões de crédito têm <strong>ciclo de fatura</strong>. Suas compras vão automaticamente
        para a fatura do mês de competência (baseada no dia de fechamento).
      </PageHint>

      {cardsQ.isLoading && <p className="text-slate-500">carregando…</p>}

      {cardsQ.data?.length === 0 && (
        <EmptyState
          title="Você ainda não tem cartões"
          hint="Cadastre seu cartão de crédito com limite, fechamento e vencimento."
          icon="💳"
          action={
            <button
              onClick={() => setOpen(true)}
              className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Cadastrar primeiro cartão
            </button>
          }
        />
      )}

      {(cardsQ.data?.length ?? 0) > 0 && (
        <OwnerFilter value={ownerFilter} onChange={setOwnerFilter} />
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visibleCards.map((c) => {
          const used = parseFloat(c.used);
          const limit = parseFloat(c.credit_limit);
          const pct = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
          return (
            <div
              key={c.id}
              className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="truncate font-medium">{c.account_name}</p>
                    <OwnerBadge ownerId={c.owner_user_id} isShared={c.is_shared} />
                  </div>
                  <p className="text-xs text-slate-500">
                    {BRAND_LABEL[c.brand]}
                    {c.last4 && ` ···· ${c.last4}`}
                  </p>
                </div>
                {c.is_primary && (
                  <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
                    principal
                  </span>
                )}
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">usado</p>
                <p className="text-lg font-semibold">{fmtBRL(c.used)}</p>
                <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  disponível {fmtBRL(c.available)} de {fmtBRL(c.credit_limit)}
                </p>
              </div>

              <div className="flex items-center justify-between border-t border-slate-100 pt-2 text-xs">
                <span className="text-slate-500">
                  {c.closing_mode === 'fixed_day'
                    ? `fech. dia ${c.closing_day} · vence dia ${c.due_day}`
                    : `fech. ${c.closing_offset_days}d antes · vence dia ${c.due_day}`}
                  {' · '}
                  <button
                    onClick={() => setEditing(c)}
                    className="tap text-slate-400 hover:text-slate-900 hover:underline"
                  >
                    editar
                  </button>
                </span>
                {c.current_invoice_id ? (
                  <Link
                    to={`/invoices/${c.current_invoice_id}`}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    fatura ({STATUS_LABEL[c.current_invoice_status ?? 'open']}) →
                  </Link>
                ) : (
                  <span className="text-slate-400">sem fatura ainda</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <CreateCardModal
        open={open}
        institutions={institutionsQ.data ?? []}
        onClose={() => setOpen(false)}
        onCreated={() => {
          setOpen(false);
          toast.success('cartão criado');
          qc.invalidateQueries({ queryKey: ['cards'] });
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />

      <EditCardModal
        card={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          toast.success('cartão atualizado');
          qc.invalidateQueries({ queryKey: ['cards'] });
          qc.invalidateQueries({ queryKey: ['accounts'] });
          qc.invalidateQueries({ queryKey: ['dashboard'] });
        }}
      />
    </div>
  );
}

function EditCardModal({
  card,
  onClose,
  onSaved,
}: {
  card: CardSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [brand, setBrand] = useState<CardBrand>('mastercard');
  const [last4, setLast4] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [closingMode, setClosingMode] = useState<ClosingMode>('fixed_day');
  const [closingDay, setClosingDay] = useState('25');
  const [closingOffsetDays, setClosingOffsetDays] = useState('14');
  const [dueDay, setDueDay] = useState('5');
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!card) return;
    setName(card.account_name);
    setBrand(card.brand);
    setLast4(card.last4 ?? '');
    setCreditLimit(card.credit_limit);
    setClosingMode(card.closing_mode);
    setClosingDay(String(card.closing_day ?? 25));
    setClosingOffsetDays(String(card.closing_offset_days ?? 14));
    setDueDay(String(card.due_day));
    setIsShared(card.is_shared);
    setError(null);
  }, [card]);

  const mutation = useMutation({
    mutationFn: async () => {
      const base = {
        name,
        brand,
        last4: last4 || null,
        credit_limit: creditLimit || '0',
        due_day: parseInt(dueDay, 10),
        closing_mode: closingMode,
        ...(closingMode === 'fixed_day'
          ? { closing_day: parseInt(closingDay, 10), closing_offset_days: null }
          : { closing_offset_days: parseInt(closingOffsetDays, 10), closing_day: null }),
      };
      await updateCard(card!.id, base);
      if (isShared !== card!.is_shared) {
        await updateAccount(card!.account_id, { is_shared: isShared });
      }
    },
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  if (!card) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal open title={`Editar "${card.account_name}"`} onClose={onClose} size="lg">
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Nome do cartão</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Bandeira</span>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value as CardBrand)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              {(Object.keys(BRAND_LABEL) as CardBrand[]).map((b) => (
                <option key={b} value={b}>
                  {BRAND_LABEL[b]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Final (4 dígitos)</span>
            <input
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              maxLength={4}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </label>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Limite de crédito (R$)</span>
          <input
            required
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setClosingMode('fixed_day')}
            className={`tap flex-1 rounded-md border px-3 py-3 text-left ${
              closingMode === 'fixed_day'
                ? 'border-slate-900 bg-slate-50 text-slate-900'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            <div className="text-sm font-medium">🗓 Dia fixo</div>
          </button>
          <button
            type="button"
            onClick={() => setClosingMode('days_before_due')}
            className={`tap flex-1 rounded-md border px-3 py-3 text-left ${
              closingMode === 'days_before_due'
                ? 'border-slate-900 bg-slate-50 text-slate-900'
                : 'border-slate-300 text-slate-600'
            }`}
          >
            <div className="text-sm font-medium">⏱ Dias antes do vencimento</div>
          </button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {closingMode === 'fixed_day' ? (
            <label className="flex flex-col gap-1.5">
              <span className="text-slate-700">Dia de fechamento</span>
              <input
                required
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={closingDay}
                onChange={(e) => setClosingDay(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
              />
            </label>
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="text-slate-700">Dias antes do vencimento</span>
              <input
                required
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={closingOffsetDays}
                onChange={(e) => setClosingOffsetDays(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
              />
            </label>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Dia de vencimento</span>
            <input
              required
              type="number"
              inputMode="numeric"
              min="1"
              max="31"
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </label>
        </div>
        <HelpText>
          Mudar fechamento/vencimento vale para as próximas faturas — as já geradas não são
          recalculadas.
        </HelpText>
        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">cartão compartilhado com household</span>
        </label>

        {error && <p className="text-red-600">{error}</p>}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'salvando…' : 'salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const BRANDS: { value: CardBrand; label: string }[] = (
  Object.keys(BRAND_LABEL) as CardBrand[]
).map((v) => ({ value: v, label: BRAND_LABEL[v] }));

function CreateCardModal({
  open,
  onClose,
  onCreated,
  institutions,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  institutions: { id: string; name: string }[];
}) {
  const [institutionId, setInstitutionId] = useState('');
  const [brand, setBrand] = useState<CardBrand>('mastercard');
  const [last4, setLast4] = useState('');
  const [creditLimit, setCreditLimit] = useState('');
  const [closingMode, setClosingMode] = useState<ClosingMode>('fixed_day');
  const [closingDay, setClosingDay] = useState('25');
  const [closingOffsetDays, setClosingOffsetDays] = useState('14');
  const [dueDay, setDueDay] = useState('5');
  const [isShared, setIsShared] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // nome padronizado: "<Instituição> Crédito"; sem instituição, "<Bandeira> Crédito"
  const instName = institutions.find((i) => i.id === institutionId)?.name ?? null;
  const derivedName = instName
    ? deriveAccountName(instName, 'credit_card')
    : `${BRAND_LABEL[brand]} Crédito`;

  const mutation = useMutation({
    mutationFn: createCard,
    onSuccess: () => {
      setInstitutionId('');
      setLast4('');
      setCreditLimit('');
      setClosingMode('fixed_day');
      setClosingDay('25');
      setClosingOffsetDays('14');
      setDueDay('5');
      setIsShared(false);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const base = {
      name: derivedName,
      institution_id: institutionId || null,
      brand,
      last4: last4 || null,
      credit_limit: creditLimit || '0',
      due_day: parseInt(dueDay, 10),
      closing_mode: closingMode,
      is_shared: isShared,
    };
    if (closingMode === 'fixed_day') {
      mutation.mutate({ ...base, closing_day: parseInt(closingDay, 10) });
    } else {
      mutation.mutate({
        ...base,
        closing_offset_days: parseInt(closingOffsetDays, 10),
      });
    }
  }

  return (
    <Modal open={open} title="Novo cartão de crédito" onClose={onClose} size="lg">
      <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Instituição</span>
          <select
            value={institutionId}
            onChange={(e) => setInstitutionId(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          >
            <option value="">— sem instituição —</option>
            {institutions.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Nome do cartão</span>
          <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-3 text-base font-medium">
            {derivedName}
          </p>
          <HelpText>
            Gerado a partir da instituição + tipo. Dá pra renomear depois em "editar".
          </HelpText>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Bandeira</span>
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value as CardBrand)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            >
              {BRANDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Final (4 dígitos)</span>
            <input
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              maxLength={4}
              placeholder="1234"
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
          </label>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-slate-700">Limite de crédito (R$)</span>
          <input
            required
            type="number"
            inputMode="decimal"
            step="0.01"
            min="0"
            value={creditLimit}
            onChange={(e) => setCreditLimit(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
          />
        </label>
        <div className="flex flex-col gap-2">
          <span className="text-slate-700">Como o cartão fecha?</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setClosingMode('fixed_day')}
              className={`tap flex-1 rounded-md border px-3 py-3 text-left ${
                closingMode === 'fixed_day'
                  ? 'border-slate-900 bg-slate-50 text-slate-900'
                  : 'border-slate-300 text-slate-600'
              }`}
            >
              <div className="text-sm font-medium">🗓 Em um dia fixo</div>
              <div className="text-[11px] text-slate-500">
                Itaú, BB, Santander…
              </div>
            </button>
            <button
              type="button"
              onClick={() => setClosingMode('days_before_due')}
              className={`tap flex-1 rounded-md border px-3 py-3 text-left ${
                closingMode === 'days_before_due'
                  ? 'border-slate-900 bg-slate-50 text-slate-900'
                  : 'border-slate-300 text-slate-600'
              }`}
            >
              <div className="text-sm font-medium">⏱ Dias antes do vencimento</div>
              <div className="text-[11px] text-slate-500">
                Sicredi, Inter, Nubank…
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {closingMode === 'fixed_day' ? (
            <label className="flex flex-col gap-1.5">
              <span className="text-slate-700">Dia de fechamento</span>
              <input
                required
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={closingDay}
                onChange={(e) => setClosingDay(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
              />
              <HelpText>Dia fixo em que a fatura fecha.</HelpText>
            </label>
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="text-slate-700">Dias antes do vencimento</span>
              <input
                required
                type="number"
                inputMode="numeric"
                min="1"
                max="31"
                value={closingOffsetDays}
                onChange={(e) => setClosingOffsetDays(e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
              />
              <HelpText>Quantos dias antes do vencimento a fatura fecha. Sicredi/Inter: 14.</HelpText>
            </label>
          )}
          <label className="flex flex-col gap-1.5">
            <span className="text-slate-700">Dia de vencimento</span>
            <input
              required
              type="number"
              inputMode="numeric"
              min="1"
              max="31"
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-3 text-base"
            />
            <HelpText>Dia em que a fatura precisa estar paga.</HelpText>
          </label>
        </div>
        <label className="tap flex items-center gap-2">
          <input
            type="checkbox"
            checked={isShared}
            onChange={(e) => setIsShared(e.target.checked)}
            className="h-5 w-5"
          />
          <span className="text-slate-700">cartão compartilhado com household</span>
        </label>

        {error && <p className="text-red-600">{error}</p>}

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="tap rounded-md border border-slate-300 px-4 py-2 sm:order-1"
          >
            cancelar
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="tap rounded-md bg-slate-900 px-4 py-2 font-medium text-white disabled:opacity-50 sm:order-2"
          >
            {mutation.isPending ? 'criando…' : 'criar cartão'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
